import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { Button } from "@moe/oss/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@moe/oss/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@moe/oss/ui/select";
import { Textarea } from "@moe/oss/ui/textarea";
import { TipBubble } from "@moe/priv/components/tip-bubble";
import { ChatSettings } from "@moe/priv/model/chat";
import { DialogConfig } from "@moe/priv/types/types";
import { toTitleCase } from "@moe/priv/utils";
import { useMutation } from "@tanstack/react-query";
import { useChatData } from "@web/route-services/chat/use-chat-data";
import { useAppContext } from "@web/route-services/root/AppContext";
import { queryClient } from "@web/routes/__root";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

interface Props {
  chatID: number;
}

/**
 * This modal allows the user to configure custom chat style settings.
 * The API settings are stored in the user's local storage and used in the chat service to generate responses.
 */
export function ResponseSettingsModal({ chatID }: Props) {
  const { createDialog, closeModal, createModal, access } = useAppContext();

  // Initialize form state with chat settings or defaults
  const [formValue, setFormValue] = useState<ChatSettings>({});

  const { data: chat } = useChatData(chatID);
  const chatSettings = chat?.settings;

  // Update form preferences when chat settings are loaded
  useEffect(() => {
    if (chatSettings) setFormValue(chatSettings);
  }, [chatSettings]);

  const updateResponsePreference = <K extends keyof ChatSettings>(key: K, value: ChatSettings[K] | "default") => {
    setFormValue((prev) => ({
      ...prev,
      // If the field setting is set to "default", we should add nothing to the preference object
      [key]: value === "default" ? undefined : value
    }));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await access.chat.update({
        id: chatID,
        settings: formValue
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats", chatID] });
      toast.success("Settings saved!");
      closeModal();
    },
    onError: () => {
      toast.error("Failed to save settings.");
    }
  });

  // Check if there are unsaved changes
  const hasUnsavedChanges = JSON.stringify(formValue) !== JSON.stringify(chatSettings);

  /**
   * Creates a dialog to confirm the reset action.
   */
  const resetPreferences = useCallback(async () => {
    setFormValue({});
    // Save default settings to backend
    try {
      await access.chat.update({
        id: chatID,
        settings: {}
      });
      queryClient.invalidateQueries({ queryKey: ["chats", chatID, "settings"] });
      toast.success("Settings reset successfully!");
    } catch (error) {
      toast.error("Failed to reset settings.");
    }
  }, [access.chat, chatID]);

  const resetButtonHandler = useCallback(() => {
    const config: DialogConfig = {
      title: "Reset Settings",
      description: "Are you sure you want to reset all settings to default?",
      actionLabel: "Reset",
      onAction: async () => {
        await resetPreferences();
      },
      onCancel: () => createModal({ content: <ResponseSettingsModal chatID={chatID} />, headerDragOnly: true })
    };
    closeModal();
    createDialog(config);
  }, [closeModal, createDialog, createModal, resetPreferences, chatID]);

  return (
    <Card className="text-tx-primary bg-float scroll-primary max-h-[75vh] w-full overflow-y-auto border-none md:w-96 md:border">
      <CardHeader>
        <CardTitle>Response style</CardTitle>
        <CardDescription>Customize the response style of their messages</CardDescription>
      </CardHeader>

      <CardContent>
        <div className="flex flex-col space-y-6">
          <SettingSelect
            settingKey="length"
            value={formValue.length}
            onValueChange={(value) => updateResponsePreference("length", value as ChatSettings["length"])}
          />
          <SettingSelect
            settingKey="narrationStyle"
            value={formValue.narrationStyle}
            onValueChange={(value) =>
              updateResponsePreference("narrationStyle", value as ChatSettings["narrationStyle"])
            }
          />
          <SettingSelect
            settingKey="pointOfView"
            value={formValue.pointOfView}
            onValueChange={(value) => updateResponsePreference("pointOfView", value as ChatSettings["pointOfView"])}
          />
          <div className="space-y-2">
            <label className="font-semibold">Custom Instructions</label>
            <Textarea
              value={formValue.customInstructions}
              onChange={(e) => updateResponsePreference("customInstructions", e.target.value)}
              placeholder="You may enter a custom prompt here (optional)."
            />
          </div>
        </div>
      </CardContent>

      <CardFooter className="flex items-center justify-end gap-6 pt-6">
        <button
          onClick={resetButtonHandler}
          className="text-tx-tertiary hover:text-tx-primary text-sm font-semibold transition-colors"
        >
          Reset
        </button>
        {/* Save Button */}
        <Button onClick={saveMutation.mutate} disabled={!hasUnsavedChanges || saveMutation.isPending}>
          <PencilSquareIcon />
          Save
        </Button>
      </CardFooter>
    </Card>
  );
}

const config = {
  length: {
    label: "Response Length",
    tipText: "How long should the character response be?",
    description: {
      default: "The default response length.",
      short: "Brief and concise responses.",
      medium: "Balanced response length.",
      long: "Detailed and elaborate responses."
    },
    options: ["default", "short", "medium", "long"]
  },
  narrationStyle: {
    label: "Narration Style",
    tipText: "How narration will be delivered.",

    description: {
      default: "The default narration style.",
      roleplay: "Has narration, character responses are in quotes.",
      text: "Casual text message-like format"
    },
    options: ["default", "roleplay", "text"]
  },
  pointOfView: {
    label: "Point of View",
    tipText: "What perspective will the narration be in?",
    description: {
      default: "The default point of view.",
      first: "Narration from the character's perspective using 'I' and 'we'.",
      second: "Directly addresses the reader using 'you'.",
      third: "Third person perspective, uses 'he', 'she', and 'they'."
    },
    options: ["default", "first", "second", "third"]
  }
};

/**
 * All possible keys for response styles, i.e "length" | "narrationStyle" | "pointOfView"
 */
type ResponseStyleK = keyof Omit<ChatSettings, "customInstructions">;
/**
 * All possible values of various response style settings
 */
type ResponseStyleV = ChatSettings[ResponseStyleK];

interface SettingSelectProps {
  settingKey: ResponseStyleK;
  value: ResponseStyleV;
  onValueChange: (value: ResponseStyleV | "default") => void;
}
/**
 * Component to handle <Select> options for response styles
 */
function SettingSelect({ settingKey, value, onValueChange }: SettingSelectProps) {
  const setting = config[settingKey];

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-base font-semibold">{setting.label}</span>
        <TipBubble text={setting.tipText} />
      </div>
      <Select value={value} onValueChange={onValueChange as any} defaultValue="default">
        <SelectTrigger className="w-full">{value ? toTitleCase(value) : "Default"}</SelectTrigger>
        <SelectContent portal={false}>
          {setting.options.map((option) => (
            <SelectItem key={option} value={option}>
              <div className="flex flex-col">
                <span className="font-medium">{toTitleCase(option)}</span>
                <span className="text-tx-primary text-sm">
                  {setting.description[option as keyof typeof setting.description]}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
